/**
 * Persistent stale-while-revalidate cache for the inbox thread list.
 *
 * The Inbox tab used to sit behind a skeleton for the whole of `GET /inbox`,
 * which is gated on OAuth refresh and provider label calls. This stores just
 * enough per thread to paint the list on the next cold session, so the network
 * round-trip becomes a background revalidate instead of a blocking load.
 *
 * Deliberately narrow:
 *  - Only list-row fields are stored. No `messages[]`, no message `body`, no
 *    tokens. Opening a thread always fetches the full payload.
 *  - Entries are keyed by user + org, so switching either misses cleanly rather
 *    than showing someone else's mail.
 *  - Nothing is read or written while impersonating (see `isCacheAllowed`).
 */

const DB_NAME = 'lighthouse-inbox'
const DB_VERSION = 1
const STORE = 'inbox_rows'

/** Bump when the persisted shape changes; old entries then miss instead of misrendering. */
const SCHEMA_VERSION = 'v1'

/** Cached mail older than this is discarded rather than shown. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

/** Per-inbox row ceiling, keeping the stored blob bounded. */
const MAX_ROWS_PER_INBOX = 200

const WRITE_DEBOUNCE_MS = 1000

/** Row fields that are enough to render a thread in the list. */
const ROW_FIELDS = [
    'id',
    'inbox',
    'snoozed',
    'relevant',
    'category',
    'triage_lane',
    'quality_score',
    'priority_override',
    'priority_source',
    'rule_filter_label',
    'message_count',
    'hidden',
    'merge_id',
    'ai_processed',
    'starred',
    'has_pending_draft',
    'has_agent_conversation',
    'last_message_date',
] as const

/** `last_message` fields the row renderer and list filters read. */
const LAST_MESSAGE_FIELDS = [
    'id',
    'to',
    'cc',
    'date',
    'from',
    'inbox',
    'thread',
    'snippet',
    'subject',
    'provider_id',
    'from_name',
    'label_ids',
    'outlook_categories',
    'outlook_flagged',
    'outlook_draft',
    'is_agent_draft',
    'ai_processed',
    'attachments',
] as const

/** Inbox-level fields needed for the sidebar and list scoping. */
const INBOX_FIELDS = [
    'id',
    'user',
    'email',
    'provider',
    'shared',
    'connected',
    'is_default',
    'imported',
    'ai_import_completed',
    'needs_reauth',
    'settings',
    'labels',
    'categories',
    'draft_count',
    'starred_count',
    'main_inbox_unread_count',
    'profile',
    // Workspace members: who connected it and what the viewer may do with it (grouping, view-only, "Sent by").
    'access_level',
    'owner_user',
    'is_mine',
    'visibility',
    'ai_mode',
] as const

export interface CachedInboxPayload {
    savedAt: number
    inboxes: any[]
}

let dbPromise: Promise<IDBDatabase | null> | null = null

function openDb(): Promise<IDBDatabase | null> {
    if (dbPromise) return dbPromise
    dbPromise = new Promise((resolve) => {
        try {
            if (typeof indexedDB === 'undefined') return resolve(null)
            const request = indexedDB.open(DB_NAME, DB_VERSION)
            request.onupgradeneeded = () => {
                const db = request.result
                if (!db.objectStoreNames.contains(STORE)) {
                    db.createObjectStore(STORE)
                }
            }
            request.onsuccess = () => resolve(request.result)
            request.onerror = () => resolve(null)
            request.onblocked = () => resolve(null)
        } catch {
            resolve(null)
        }
    })
    return dbPromise
}

function idbGet(key: string): Promise<CachedInboxPayload | null> {
    return openDb().then(
        (db) =>
            new Promise((resolve) => {
                if (!db) return resolve(null)
                try {
                    const request = db.transaction(STORE, 'readonly').objectStore(STORE).get(key)
                    request.onsuccess = () => resolve((request.result as CachedInboxPayload) ?? null)
                    request.onerror = () => resolve(null)
                } catch {
                    resolve(null)
                }
            }),
    )
}

function idbSet(key: string, value: CachedInboxPayload): Promise<void> {
    return openDb().then(
        (db) =>
            new Promise((resolve) => {
                if (!db) return resolve()
                try {
                    const request = db.transaction(STORE, 'readwrite').objectStore(STORE).put(value, key)
                    request.onsuccess = () => resolve()
                    request.onerror = () => resolve()
                } catch {
                    resolve()
                }
            }),
    )
}

function idbClearAll(): Promise<void> {
    return openDb().then(
        (db) =>
            new Promise((resolve) => {
                if (!db) return resolve()
                try {
                    const request = db.transaction(STORE, 'readwrite').objectStore(STORE).clear()
                    request.onsuccess = () => resolve()
                    request.onerror = () => resolve()
                } catch {
                    resolve()
                }
            }),
    )
}

function cacheKey(userId: string, orgId: string): string {
    return `${userId}:${orgId}:${SCHEMA_VERSION}`
}

function pick(source: any, fields: readonly string[]): any {
    const out: any = {}
    for (const field of fields) {
        if (source?.[field] !== undefined) out[field] = source[field]
    }
    return out
}

/**
 * Impersonation is sessionStorage-scoped, so a support session shares this
 * origin's IndexedDB with the staff member's own account. Never touch the cache
 * in that mode, in either direction.
 */
function isCacheAllowed(): boolean {
    try {
        return !sessionStorage.getItem('lh_impersonation')
    } catch {
        return false
    }
}

function isTourDemoThread(thread: any): boolean {
    return typeof thread?.id === 'string' && thread.id.includes('tour-demo')
}

function toSlimRow(thread: any): any {
    const row = pick(thread, ROW_FIELDS)
    if (thread.last_message) {
        row.last_message = pick(thread.last_message, LAST_MESSAGE_FIELDS)
    }
    return row
}

function toSlimInbox(inbox: any): any {
    const slim = pick(inbox, INBOX_FIELDS)
    const threads = (inbox.threads ?? [])
        .filter((t: any) => !isTourDemoThread(t) && t?.id && t.last_message)
        .slice(0, MAX_ROWS_PER_INBOX)
        .map(toSlimRow)
    slim.threads = threads
    return slim
}

/**
 * Read cached inboxes for this user+org.
 *
 * Threads come back marked `_fromCache`, which callers use to know that
 * `messages` was never persisted and must be fetched before rendering a thread
 * detail, and to reconcile rows the server no longer returns.
 */
export async function readInboxCache(
    userId: string | undefined,
    orgId: string | undefined,
): Promise<any[] | null> {
    if (!userId || !orgId || !isCacheAllowed()) return null
    const payload = await idbGet(cacheKey(userId, orgId))
    if (!payload?.inboxes?.length) return null
    if (Date.now() - (payload.savedAt ?? 0) > MAX_AGE_MS) return null
    // Nothing paintable — let the caller show the skeleton instead.
    if (!payload.inboxes.some((i: any) => (i?.threads?.length ?? 0) > 0)) return null

    for (const inbox of payload.inboxes) {
        for (const thread of inbox.threads ?? []) {
            thread._fromCache = true
        }
    }
    return payload.inboxes
}

let writeTimer: ReturnType<typeof setTimeout> | null = null

/** Persist the current inbox list. Debounced; failures are non-fatal. */
export function writeInboxCache(
    userId: string | undefined,
    orgId: string | undefined,
    inboxes: any[] | undefined,
): void {
    if (!userId || !orgId || !Array.isArray(inboxes) || !isCacheAllowed()) return
    // Callers that fetch inboxes without threads (settings pages, the
    // labels-only refetch on a cold load) would otherwise persist an empty
    // snapshot, which the next session would paint as "no mail" instead of
    // falling back to the skeleton.
    if (!inboxes.some((i: any) => (i?.threads?.length ?? 0) > 0)) return
    if (writeTimer) clearTimeout(writeTimer)
    writeTimer = setTimeout(() => {
        writeTimer = null
        try {
            void idbSet(cacheKey(userId, orgId), {
                savedAt: Date.now(),
                // `pick` copies nested values (labels, settings, categories) as Vue
                // reactive proxies, which IndexedDB can't structured-clone: every
                // write failed with a DataCloneError. A JSON round-trip stores
                // plain data.
                inboxes: JSON.parse(JSON.stringify(inboxes.map(toSlimInbox))),
            })
        } catch {
            /* cache writes are best-effort */
        }
    }, WRITE_DEBOUNCE_MS)
}

/** Drop everything. Called on logout, inbox disconnect, and org switch. */
export async function clearInboxCache(): Promise<void> {
    if (writeTimer) {
        clearTimeout(writeTimer)
        writeTimer = null
    }
    await idbClearAll()
}
